import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  UsersRound,
  MessagesSquare,
  Phone,
  Users,
  Settings,
  Building2,
  BarChart3,
  Activity,
} from 'lucide-react';
import { formatAgentLabel } from '../config/agents';
import { getAvailabilityStatusClass } from '../utils/presence';
import { isDesktopNavigationPathAllowed } from '../utils/desktopMode';

const navItems = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, roles: ['admin'] },
  { label: 'Personal Chat', to: '/internal-chat', icon: MessageSquare, roles: ['admin', 'agent'] },
  { label: 'Team Chat', to: '/internal-teams', icon: UsersRound, roles: ['admin', 'agent'] },
  { label: 'SMS / MMS', to: '/sms-mms', icon: MessagesSquare, roles: ['admin', 'agent'] },
  { label: 'Calls', to: '/calls', icon: Phone, roles: ['admin', 'agent'] },
  { label: 'Queue Dashboard', to: '/call-queues', icon: Activity, roles: ['admin', 'agent'] },
  { label: 'Directory', to: '/users', icon: Users, roles: ['admin', 'platform_admin', 'reseller_admin', 'client_admin', 'client_user', 'agent'] },
  { label: 'Reseller Portal', to: '/admin-portal', icon: Building2, roles: ['admin'] },
  { label: 'Settings', to: '/settings', icon: Settings, roles: ['admin'] },
  { label: 'Report Manager', to: '/settings/reports', icon: BarChart3, roles: ['admin', 'platform_admin', 'client_admin'] },
];

export const getVisibleSidebarItems = ({ isDesktopMode, userRole, authRole }) => {
  const normalizedUserRole = String(userRole || '').trim().toLowerCase();
  const normalizedAuthRole = String(authRole || '').trim().toLowerCase();

  return navItems.filter((item) => (
    (item.roles.includes(normalizedUserRole) || item.roles.includes(normalizedAuthRole))
    && (!isDesktopMode || isDesktopNavigationPathAllowed(item.to))
  ));
};

function Sidebar({
  isDesktopMode = false,
  userRole = 'admin',
  onRoleChange,
  roleLocked = false,
  unreadTotals = {},
  authUser,
  onLogout,
  deviceStatus,
  agentId,
  agentStatus,
  availabilityStatus,
  availabilityOptions = [],
  onAvailabilityStatusChange,
  isUpdatingStatus = false,
  statusUpdateError = '',
}) {
  const navigate = useNavigate();
  const visibleItems = getVisibleSidebarItems({
    isDesktopMode,
    userRole,
    authRole: authUser?.role,
  });
  const displayName = authUser?.name || formatAgentLabel(agentId);
  const roleLabel = formatRoleLabel(authUser?.role || userRole);
  const statusClass = getAvailabilityStatusClass(agentStatus);
  const voiceStatus = getVoiceStatus(deviceStatus);

  const getUnreadCount = (path) => {
    if (path === '/internal-chat') {
      return normalizeUnreadCount(unreadTotals.internalChat);
    }

    if (path === '/internal-teams') {
      return normalizeUnreadCount(unreadTotals.internalTeams);
    }

    return 0;
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-top">
        <div className="brand">
          <div className="brand-mark">V</div>
          <div className="brand-copy">
            <div className="brand-name">KAYLAD</div>
            <div className="brand-subtitle">Premium VoIP Suite</div>
          </div>
        </div>
      </div>

      <div className="sidebar-nav-scroll">
        <nav className="sidebar-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
              end={item.to === '/' || item.to === '/settings'}
            >
              <span className="nav-icon" aria-hidden="true">
                <item.icon size={18} />
              </span>
              <span className="nav-item-label">{item.label}</span>
              {getUnreadCount(item.to) > 0 ? (
                <span className="nav-badge" aria-label={`${getUnreadCount(item.to)} unread items`}>
                  {formatUnreadBadge(getUnreadCount(item.to))}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-account-summary"
          onClick={() => navigate('/profile')}
          aria-label="Open user profile"
        >
          <div className="sidebar-account-name">{displayName}</div>

          {!isDesktopMode ? (
            <div className={`sidebar-voice-status ${voiceStatus.className}`}>
              <span className="sidebar-voice-dot" aria-hidden="true" />
              <span>{voiceStatus.label}</span>
            </div>
          ) : null}

          <div className="sidebar-account-role">
            Role: <strong>{roleLabel}</strong>
          </div>
        </button>

        {!roleLocked ? (
          <label className="sidebar-account-field">
            <span>Change role</span>
            <select
              value={userRole}
              onChange={(event) => onRoleChange?.(event.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="agent">Agent</option>
            </select>
          </label>
        ) : null}

        <label className={`sidebar-status-select ${statusClass}`}>
          <span className={`sidebar-status-dot ${statusClass}`} aria-hidden="true" />
          <span>Status</span>
          <select
            value={availabilityStatus}
            onChange={(event) => onAvailabilityStatusChange?.(event.target.value)}
            aria-label="Update your availability status"
            disabled={isUpdatingStatus}
          >
            {availabilityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {isUpdatingStatus ? <em>Updating...</em> : null}
        </label>

        {statusUpdateError ? (
          <div className="sidebar-account-error" role="status" aria-live="polite">
            {statusUpdateError}
          </div>
        ) : null}

        <button type="button" className="sidebar-logout" onClick={onLogout}>
          Logout
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;

function normalizeUnreadCount(value) {
  const count = Number(value || 0);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function formatUnreadBadge(count) {
  return count > 99 ? '99+' : String(count);
}

function formatRoleLabel(role) {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'agent' || normalized === 'client_user') return 'Agent';
  if (normalized === 'platform_admin') return 'Admin';
  if (normalized === 'reseller_admin') return 'Reseller Admin';
  if (normalized === 'client_admin') return 'Client Admin';

  return normalized
    ? normalized.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
    : 'Admin';
}

function getVoiceStatus(deviceStatus) {
  switch (deviceStatus) {
    case 'ready':
      return { label: 'Voice Ready', className: 'is-ready' };
    case 'initializing':
      return { label: 'Voice Connecting', className: 'is-connecting' };
    case 'error':
      return { label: 'Voice Error', className: 'is-error' };
    default:
      return { label: 'Voice Offline', className: 'is-offline' };
  }
}
